/**
 * <collection-product-count>
 *
 * Keeps the number next to the collection title in step with the active filters.
 *
 * The header is its own section, so Dawn's facet AJAX (`assets/facets.js`) never
 * re-renders it — `renderPage` only fetches the product grid section and, from
 * that response, refills the `#ProductCount` span that `snippets/facets.liquid`
 * renders. Watching that span is what keeps this figure in sync without patching
 * facets.js: `renderProductCount` writes the span's text and then its
 * `data-product-count` attribute, both of which land in one observer callback.
 *
 * `#ProductCount` only exists while filtering or sorting is enabled. Without it
 * the count stays at the server-rendered value, which is the right answer anyway.
 */
class CollectionProductCount extends HTMLElement {
  connectedCallback() {
    this.valueElement = this.querySelector('.collection-header__count-value');
    this.labelElement = this.querySelector('.collection-header__count-label');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.watchSource(), { once: true });
    } else {
      this.watchSource();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  watchSource() {
    // The product grid section renders below this one, so the span is only
    // reachable once the document has been parsed.
    this.source = document.getElementById('ProductCount');
    if (!this.source) return;

    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.source, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-product-count'],
    });
  }

  sync() {
    const count = this.source.dataset.productCount;
    if (count === undefined || count === '') return;

    if (this.valueElement) this.valueElement.textContent = count;
    if (this.labelElement) this.labelElement.textContent = this.source.textContent.trim();
  }
}

if (!customElements.get('collection-product-count')) {
  customElements.define('collection-product-count', CollectionProductCount);
}
